// Compliance — "hvordan understøtter jeres Microsoft 365 kravene i CIS 18, ISO 27001, NIS2 og GDPR?"
// One page, one job: requirements, their status and the evidence behind it. A tile per framework (and Microsoft's own
// Secure Score) on top; under it one section per framework, and in each the requirements as cards by status
// (Ikke understøttet, Delvist understøttet, Understøttet). The same design as Gennemgang and Konfiguration: the chosen
// requirement in the side panel with the technical checks behind it, and decisions made in the decision window.
// The only way out is "Åbn politikken i Konfiguration", where the policy behind a check is read.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantData } from '../context/TenantDataContext';
import { useResizablePanel } from '../hooks/useResizablePanel';
import { useToolsAlign } from '../hooks/useToolsAlign';
import { useCachedFetch } from '../hooks/useCachedFetch';
import { useAuth } from '../context/AuthContext';
import LoadingState from '../components/LoadingState';
import NcIcon from '../components/NcIcon';
import PortalIcon from '../components/PortalIcon';
import SearchInput from '../components/SearchInput';
import ExpandAll from '../components/ExpandAll';
import FocusToggle, { useFocusMode } from '../components/FocusToggle';
import { useItList } from '../hooks/useItList';
import { cardItProps, itKeyOf } from '../utils/itCard';
import { plainText } from '../utils/plainText';
import DecisionTag from '../components/DecisionTag';
import { DECISION_LABEL } from '../components/DecisionTypeRadios';
import { TileMenu, StatusCard, OnePagePanel } from '../components/StatusCards';
import { getStatusBadge } from '../utils/statusBadge';
import { decisionsByItem } from '../utils/decisions';
import { ageText, freshnessOf, useSyncStatus } from '../utils/freshness';
import { AREA_ICON, areaLabel } from '../data/areaLabels';
import {
  FRAMEWORKS, FRAMEWORK_LABEL, areaOfControl, areaOfService,
  buildClauses, clauseStats, evidenceFacts, problemText,
} from '../data/complianceModel';
import { CANNOT_FETCH, effectiveStatus, itemsByIdFrom } from '../utils/policyDisplay';
import './styles.css';
import './WorkQueueGroups.css';
import './CompliancePage.css';

const ST = {
  covered: { label: 'Understøttet', tone: 'ok' },
  partial: { label: 'Delvist understøttet', tone: 'warn' },
  notFound: { label: 'Ikke understøttet', tone: 'bad' },
  unknown: { label: 'Kan ikke hentes', tone: 'neutral' },
  external: { label: 'Ikke i Microsoft 365', tone: 'neutral' },
  unlicensed: { label: 'Kræver licens', tone: 'neutral' },
};
// The requirement's own status (Understøttet/Delvist understøttet/Ikke understøttet) is the strict "every technical control
// met" rule; this is only how a percentage is coloured at a glance, so a framework that is doing well reads as green
// instead of amber the moment one requirement isn't 100%. No official standard for the cutoff; 75/55 is a common split.
const pctTone = (pct) => (pct === null ? null : pct >= 75 ? 'ok' : pct >= 55 ? 'warn' : 'bad');
const STATUS_TABS = [['all', 'Alle'], ['notFound', 'Ikke understøttet'], ['partial', 'Delvist'], ['covered', 'Understøttet']];
const DOT_TONE = { covered: 'ok', partial: 'warn', notFound: 'bad' };
const CARDS = [['notFound', 'Ikke understøttet', 'bad'], ['partial', 'Delvist understøttet', 'warn'], ['covered', 'Understøttet', 'ok'], ['other', 'Kan ikke vurderes', 'other']];
const FUND_ROWS = 4;

// One icon and one colour per framework (our own drawings), the same chip as the service menu on Konfiguration.
const FW_STYLE = {
  all: { bg: '#EEEDFD', fg: '#4446C6', icon: <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></> },
  cis18: { bg: '#E7F0FF', fg: '#2D63C8', icon: <><path d="M12 3l8 3v6c0 4.5-3.2 8-8 9-4.8-1-8-4.5-8-9V6z" /><path d="M9 12l2 2 4-4" /></> },
  iso: { bg: '#E3F4EC', fg: '#177A5B', icon: <><circle cx="12" cy="9" r="5" /><path d="M9 13.5L8 21l4-2 4 2-1-7.5" /></> },
  nis2: { bg: '#FBEEDD', fg: '#C97A00', icon: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3.2 3 14.8 0 18M12 3c-3 3.2-3 14.8 0 18" /></> },
  gdpr: { bg: '#E0F2F4', fg: '#0E7C86', icon: <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 018 0v3" /></> },
  microsoft: { bg: '#EDEAF3', fg: '#6F5BB5', icon: <><path d="M4 17a8 8 0 1116 0" /><path d="M12 17l4-5" /><path d="M8 20h8" /></> },
};
const FW_TEXT = {
  iso: 'Den internationale standard for informationssikkerhed.',
  nis2: 'EU-direktivet om cybersikkerhed i vigtige og væsentlige virksomheder.',
  gdpr: 'Persondataforordningen: sikkerhed ved behandlingen og anmeldelse af brud.',
  cis18: 'Praktiske sikkerhedskontroller, prioriteret efter effekt.',
  microsoft: 'Microsofts egen vurdering af jeres sikkerhedsopsætning.',
};

const doneOf = (clause) => clause.controls.filter((c) => c.eff.status === 'covered').length;
const countOf = (clause) => clause.controls.filter((c) => c.eff.status !== 'external' && c.eff.status !== 'unlicensed').length;
const clauseKey = (c) => `${c.fw}:${c.code}`;
const cardOf = (status) => (status === 'covered' || status === 'partial' || status === 'notFound' ? status : 'other');
const pctText = (cov, digits = 0) => (cov === null ? '—' : `${cov.toLocaleString('da-DK', { minimumFractionDigits: digits, maximumFractionDigits: digits })} %`);
// A card that stands under the Anbefaling ("Hvad er det"), closed to begin with.
const pre = (c) => (c ? { ...c, pre: true, open: false } : c);
const proseCard = (key, title, text) => (text ? { key, title, count: null, summary: text, body: <div className="pn-pad"><p className="pn-prose">{text}</p></div> } : null);
const factsCard = (key, title, rows) => (rows.length ? {
  key,
  title,
  count: rows.length,
  summary: `${rows[0][1]}${rows.length > 1 ? ` · +${rows.length - 1} mere` : ''}`,
  body: <div className="pn-list">{rows.map(([label, value, mono]) => <div className="pn-row" key={label}><span>{label}</span><b className={mono ? 'pn-mono' : undefined}>{value}</b></div>)}</div>,
} : null);

/* ---------- the chosen requirement ---------- */
// Policy categories that have a panel under Konfiguration, Politikker (the link target).
const POLICY_PANEL_CATEGORIES = ['compliance', 'configuration', 'settingsCatalog', 'endpointSecurity', 'adminTemplate', 'update', 'autopilot', 'enrollment', 'appProtection', 'appConfig', 'mail'];

const CHEV = <svg className="cmp-kr__chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>;
const ARROW = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>;

// Where a control lives. The Entra tenant settings are not login rules, so they say Entra.
const controlArea = (c) => (c.id.startsWith('entra-') ? 'Entra' : areaLabel(areaOfControl(c)));

// One technical control: a row with only its name. Opened, it shows what was found: the settings that are not as required
// (what they are now), and the policies and login rules that support it with their status; a policy that has a panel under
// Konfiguration opens there when pressed. The area is only said when the controls of the group are in different areas.
function Check({ control, ctx, tenantId, showArea }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const facts = control.evidence.map((e) => evidenceFacts(e, ctx));
  const status = control.eff.status;
  const settings = facts.filter((f) => f.isSetting);
  const rules = facts.filter((f) => !f.isSetting);
  const badSettings = settings.filter((f) => f.partial || f.state !== 'Aktiv');
  const fineSettings = settings.length - badSettings.length;
  // One setting that is the control itself (same name): the table only says what it is now, not the name a second time.
  const norm = (t) => String(t || '').toLowerCase().replace(/[^a-zæøå0-9]+/g, ' ').trim();
  const sameAsTitle = badSettings.length === 1 && norm(badSettings[0].name) === norm(control.title);
  const asValue = (t) => { const v = String(t || ''); return v.charAt(0).toUpperCase() + v.slice(1); };
  const sortedRules = [...rules].sort((x, y) => Number(y.partial || y.state !== 'Aktiv') - Number(x.partial || x.state !== 'Aktiv'));
  const expandable = facts.length > 0 ? badSettings.length > 0 || rules.length > 0 : status !== 'covered';
  const statusOf = (f) => (f.state !== 'Aktiv' ? f.state : f.partial ? (f.cover || 'Delvist') : 'Aktiv');
  const goTo = (f) => navigate(`/tenant/${tenantId}/configuration?policy=${encodeURIComponent(`pol:${f.policyRef.category}:${f.policyRef.id}`)}&evidence=${encodeURIComponent(control.title)}`);
  return (
    <div className={`cmp-kr${open ? ' is-open' : ''}`}>
      <button type="button" className="cmp-kr__h" aria-expanded={expandable ? open : undefined} disabled={!expandable} onClick={() => setOpen((v) => !v)}>
        <span className="cmp-kr__t">{control.title}</span>
        {showArea && <span className="cmp-kr__a">{controlArea(control)}</span>}
        {expandable ? CHEV : <span className="cmp-kr__nochev" />}
      </button>
      {open && (
        <div className="cmp-kr__x">
          {badSettings.length > 0 && (
            <div className="cmp-tb">
              {sameAsTitle ? <div className="cmp-tb__h cmp-tb__h--one"><span>Nu</span></div> : <div className="cmp-tb__h"><span>Indstilling</span><span>Nu</span></div>}
              {badSettings.map((f, i) => (sameAsTitle
                ? <div className="cmp-tb__r cmp-tb__r--one" key={`${f.name}-${i}`}><b>{asValue(f.cover || f.state)}</b></div>
                : <div className="cmp-tb__r" key={`${f.name}-${i}`}><span>{f.name}</span><b>{asValue(f.cover || f.state)}</b></div>))}
              {fineSettings > 0 && <div className="cmp-tb__f">{fineSettings === 1 ? '1 anden indstilling opfylder kravet' : `${fineSettings} andre indstillinger opfylder kravet`}</div>}
            </div>
          )}
          {sortedRules.length > 0 && (
            <div className="cmp-tb">
              <div className="cmp-tb__h"><span>Politik</span><span>Status</span></div>
              {sortedRules.map((f, i) => {
                const canOpen = f.policyRef && POLICY_PANEL_CATEGORIES.includes(f.policyRef.category);
                const st = statusOf(f);
                const body = (<><span>{f.name}</span><b className={st === 'Aktiv' ? 'is-ok' : 'is-w'}>{st}</b>{canOpen ? ARROW : <i />}</>);
                return canOpen
                  ? <button type="button" key={`${f.name}-${i}`} className="cmp-tb__r cmp-tb__r--go" onClick={() => goTo(f)}>{body}</button>
                  : <div key={`${f.name}-${i}`} className="cmp-tb__r cmp-tb__r--p">{body}</div>;
              })}
            </div>
          )}
          {facts.length === 0 && <p className="cmp-txt">{problemText(control, facts)}</p>}
        </div>
      )}
    </div>
  );
}

// The decision pinned at the bottom of every panel.
function decisionOf(note, user) {
  const decidedLabel = note?.decisionType ? DECISION_LABEL[note.decisionType] : null;
  const who = note && (note.authorName && note.authorName === user?.name ? 'Du' : note.authorName);
  return { tag: decidedLabel, tagKey: note?.decisionType, meta: note ? [who, new Date(note.createdAt).toLocaleDateString('da-DK')].filter(Boolean).join(' · ') : '', label: '', onOpen: undefined };
}

function ClausePanel({ clause, ctx, tenantId, note }) {
  const { user } = useAuth();
  const sync = useSyncStatus(tenantId);
  const fresh = freshnessOf(sync.routes, null, sync.now);
  // The controls by status: what is not supported first, then partly, then supported. Within a status, by area.
  const groups = useMemo(() => CARDS.map(([key, label, gtone]) => ({
    key,
    label,
    tone: gtone,
    controls: clause.controls.map((c, i) => [c, i]).filter(([c]) => cardOf(c.eff.status) === key).sort(([a, i], [b, j]) => controlArea(a).localeCompare(controlArea(b), 'da') || i - j).map(([c]) => c),
  })).filter((g) => g.controls.length > 0), [clause]);
  const tone = ST[clause.status].tone;
  const open = clause.controls.filter((c) => c.eff.status === 'notFound' || c.eff.status === 'partial');
  const done = doneOf(clause);
  const count = countOf(clause);
  // Said once for the whole requirement, so it is not repeated on every control below.
  const situation = clause.status === 'covered' && open.length === 0
    ? 'Alle kontroller bag kravet er understøttet af jeres politikker og loginregler.'
    : open.length > 0 ? (done === 0 ? 'Ingen understøttende kontroller fundet.' : `${done} af ${count} kontroller er understøttet.`) : null;
  const missing = open.filter((c) => c.eff.status === 'notFound').length;
  const partly = open.filter((c) => c.eff.status === 'partial').length;
  // One sentence on what to do. The count stands on the cards below, so it is not said here.
  const setUp = missing === 1 ? 'Sæt den manglende kontrol op' : 'Sæt de manglende kontroller op';
  const finish = partly === 1 ? 'gør den delvist understøttede færdig' : 'gør de delvist understøttede færdige';
  const recommend = open.length > 0
    ? [missing && partly ? `${setUp}, og ${finish}.` : missing ? `${setUp}.` : `${finish.charAt(0).toUpperCase()}${finish.slice(1)}.`, 'Har I løst det på en anden måde, så dokumentér det.'].join(' ')
    : null;
  const tk = { bad: 'r', warn: 'o', ok: 'g', neutral: 'n' }[tone];
  // When the requirement is not fully supported, the count of controls stands in the red box.
  const warn = clause.status !== 'covered' && open.length > 0
    ? { text: situation }
    : null;
  const evidence = [
    ...(clause.official ? [['Officielt navn', clause.official]] : []),
    ...(clause.ig ? [['Implementeringsgruppe', `IG${clause.ig}${clause.ig === 1 ? ' (grundlæggende)' : clause.ig === 2 ? ' (udvidet)' : ' (avanceret)'}`]] : []),
    ...(ctx.caById.size || ctx.itemsById.size ? [['Gennemgået', [ctx.caById.size ? `${ctx.caById.size} loginregler` : null, ctx.itemsById.size ? `${ctx.itemsById.size} politikker` : null].filter(Boolean).join(' · ')]] : []),
    ...(fresh ? [['Data hentet', `for ${ageText(fresh.ms)} siden`]] : []),
  ];
  // Under the Anbefaling: what the requirement is. Under Detaljer: one card per status (what is not supported first), then the evidence.
  const firstOpen = groups.find((g) => g.key === 'notFound' || g.key === 'partial')?.key;
  const cards = [
    pre(proseCard('what', 'Hvad er det', clause.desc)),
    ...groups.map((g) => {
      const areas = new Set(g.controls.map(controlArea));
      return {
        key: `st-${g.key}`,
        title: g.label,
        tone: g.tone === 'other' ? undefined : g.tone,
        count: g.controls.length,
        open: g.key === firstOpen,
        body: <div className="cmp-kl">{g.controls.map((c) => <Check key={c.id} control={c} ctx={ctx} tenantId={tenantId} showArea={areas.size > 1} />)}</div>,
      };
    }),
    factsCard('ev', 'Evidens', evidence),
  ].filter(Boolean);
  return (
    <OnePagePanel
      title={clause.title}
      sub={`${FRAMEWORK_LABEL[clause.fw]} · ${clause.label.replace('CIS ', '')}${clause.ig ? ` · IG${clause.ig}` : ''}`}
      tiles={[['Status', ST[clause.status].label, tk], ['Framework', FRAMEWORK_LABEL[clause.fw], 'n'], ...(warn ? [] : [['Kontroller', `${done} af ${count}`, 'n']])]}
      warn={warn}
      note={recommend ? { tone: tone === 'bad' ? 'bad' : 'warn', title: 'Anbefaling', text: recommend } : (clause.status === 'covered' && situation ? { tone: 'ok', title: 'Status', text: situation } : null)}
      cards={cards}
      decision={decisionOf(note, user)}
    />
  );
}

// One of Microsoft's own Secure Score controls. The scores and texts are Microsoft's own and shown unchanged (titles
// and recommendations stay in English); only the status words are ours, like everywhere else.
function ControlPanel({ control: c, note }) {
  const { user } = useAuth();
  const tone = ST[c.status]?.tone || 'neutral';
  const tk = { bad: 'r', warn: 'o', ok: 'g', neutral: 'n' }[tone];
  const evidence = [
    ...(c.userImpact ? [['Påvirkning af brugerne', c.userImpact]] : []),
    ...(c.implementationCost ? [['Indsats', c.implementationCost]] : []),
    ['Point', `${c.score} / ${c.maxScore}`],
    ...(c.service ? [['Tjeneste', c.service]] : []),
    ['Kontrol-ID', c.id, true],
    ...(c.microsoftNote ? [['Markeret i Microsoft-portalen', c.microsoftNote]] : []),
  ];
  return (
    <OnePagePanel
      title={plainText(c.title)}
      sub={`Microsoft Secure Score · ${areaLabel(areaOfService(c.service))}${c.service ? ` · ${c.service}` : ''}`}
      tiles={[['Status', ST[c.status]?.label || '—', tk], ['Point', `${c.score} af ${c.maxScore}`, 'n'], ['Tjeneste', c.service || 'Microsoft 365', 'n']]}
      warn={c.status === 'notFound' || c.status === 'partial' ? { text: `${ST[c.status].label} · ${c.score} af ${c.maxScore} point` } : null}
      note={c.status !== 'covered' && c.remediation ? { tone: tone === 'bad' ? 'bad' : 'warn', title: 'Anbefaling fra Microsoft', text: plainText(c.remediation) } : null}
      cards={[
        pre(proseCard('what', 'Hvad er det', 'En kontrol fra Microsoft Secure Score. Titlen og anbefalingen er Microsofts egne og står på engelsk, og vi viser dem uden at ændre dem.')),
        factsCard('ev', 'Om kontrollen', evidence),
      ].filter(Boolean)}
      decision={decisionOf(note, user)}
    />
  );
}


export default function CompliancePage() {
  const { tenant, loading, ca } = useTenantData();
  const itList = useItList(tenant?.tenantId);
  const [tab, setTab] = useState('microsoft'); // Secure Score is what opens first
  const [status, setStatus] = useState('all');
  const [fw, setFw] = useState(null);
  const [search, setSearch] = useState('');
  const [selId, setSelId] = useState(null);
  const [fold, setFold] = useState({});
  const [showAll, setShowAll] = useState(() => new Set());
  const [picked, setPicked] = useState(() => new Set());
  // Fokus: while it is on, the cards you are not working in step back (the same switch as Gennemgang's).
  const [focusOn, toggleFocus] = useFocusMode();
    const detailPanel = useResizablePanel(528, 340, 1000);
  const [pageRef, toolsStyle] = useToolsAlign();
  const tid = tenant?.tenantId;
  const navigate = useNavigate();
  const inv = useCachedFetch(tenant ? `/api/policy-inventory?tenantId=${tid}` : null, { skip: !tenant });
  const ctl = useCachedFetch(tenant ? `/api/controls?tenantId=${tid}` : null, { skip: !tenant });
  const ss = useCachedFetch(tenant ? `/api/secure-score-controls?tenantId=${tid}` : null, { skip: !tenant });
  const notesReq = useCachedFetch(tenant ? `/api/audit-notes?tenantId=${tid}` : null, { skip: !tenant });
  const decided = useMemo(() => decisionsByItem(notesReq.data?.notes, 'cmp:'), [notesReq.data]);
  const scope = useCachedFetch(tenant ? `/api/policy-scope?tenantId=${tid}` : null, { skip: !tenant });

  const itemsById = useMemo(() => itemsByIdFrom(inv.data), [inv.data]);
  const caById = useMemo(() => new Map((ca?.policies || []).map((p) => [p.id, p])), [ca]);
  const controls = useMemo(
    () => (ctl.data && !ctl.data.error ? ctl.data.controls.map((c) => ({ ...c, eff: effectiveStatus(c, itemsById, scope) })) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctl.data, itemsById, scope.data],
  );
  const byFw = useMemo(() => Object.fromEntries(FRAMEWORKS.map((f) => [f.key, buildClauses(controls, f.key)])), [controls]);
  // Policies NordComply has read but that no check looks at: they are not part of the assessment, and the page says so
  // (a new kind of policy that no check knows yet would otherwise be invisible).
  const unassessed = useMemo(() => {
    if (!ctl.data || ctl.data.error || !inv.data) return null;
    const used = new Set(ctl.data.controls.flatMap((c) => (c.evidence || []).map((e) => e.id)));
    const all = [...itemsById.values()];
    return { count: all.filter((i) => !used.has(i.id)).length, total: all.length };
  }, [ctl.data, inv.data, itemsById]);

  // Unlike the four frameworks, Microsoft's number is points achieved over points possible, the same figure the Microsoft
  // portal shows as "Secure Score" (two decimals, as Microsoft shows it).
  const msControls = ss.data && !ss.data.error && Array.isArray(ss.data.controls) ? ss.data.controls : null;
  const microsoftCoverage = useMemo(() => {
    if (!msControls || msControls.length === 0) return null;
    const max = msControls.reduce((sum, c) => sum + (c.maxScore || 0), 0);
    const got = msControls.reduce((sum, c) => sum + (c.score || 0), 0);
    return max > 0 ? Math.round((got / max) * 10000) / 100 : null;
  }, [msControls]);
  const covOf = (key) => (key === 'all' ? clauseStats(FRAMEWORKS.flatMap((f) => byFw[f.key] || [])).coverage : clauseStats(byFw[key] || []).coverage);

  // What the list shows: one section per framework (or Microsoft's own), the requirements in cards by status.
  const sections = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cardsOf = (sec, rows, noun) => CARDS.map(([st, title, tone]) => {
      const list = rows.filter((r) => cardOf(r.status) === st);
      return { key: `${sec}:${st}`, tone, title, rows: list, pill: { tone: tone === 'other' ? 'neutral' : tone, text: `${list.length} ${noun}` }, defaultFolded: st === 'covered' || st === 'other' };
    }).filter((c) => c.rows.length > 0);
    if (tab === 'microsoft') {
      const rows = (msControls || [])
        .filter((c) => (status === 'all' || c.status === status) && (!q || `${c.title} ${c.service || ''}`.toLowerCase().includes(q)))
        .map((c) => ({ id: `ms:${c.id}`, itemKey: `ms:${c.id}`, findingKey: 'cmp:ms', kind: 'ms', control: c, title: c.title, code: '', status: c.status, area: areaOfService(c.service), value: `${c.score} / ${c.maxScore} point`, sub: c.service || 'Microsoft 365' }));
      return rows.length ? [{ key: 'ms', title: 'Microsoft Secure Score', text: FW_TEXT.microsoft, color: FW_STYLE.microsoft.fg, extra: `${pctText(microsoftCoverage, 2)} dækning`, count: rows.length, cards: cardsOf('ms', rows, rows.length === 1 ? 'kontrol' : 'kontroller') }] : [];
    }
    const keep = (c) => (status === 'all' || c.status === status) && (!q || `${c.title} ${c.label} ${FRAMEWORK_LABEL[c.fw]}`.toLowerCase().includes(q));
    const rowsOf = (f) => (byFw[f.key] || []).filter(keep).map((c) => ({ id: clauseKey(c), itemKey: clauseKey(c), findingKey: `cmp:${c.fw}`, kind: 'clause', clause: c, title: c.title, code: c.label.replace('CIS ', ''), status: c.status, area: areaOfControl(c.controls[0]) }));
    const shown = FRAMEWORKS.filter((f) => !fw || f.key === fw);
    const rows = shown.flatMap(rowsOf);
    // No heading per framework: the menu already says which one is chosen, and in Alle each row carries its framework.
    return rows.length ? [{ key: fw || 'all', count: rows.length, cards: cardsOf(fw || 'all', rows, 'krav') }] : [];
  }, [tab, fw, status, search, byFw, msControls, microsoftCoverage]);
  const allCards = useMemo(() => sections.flatMap((s) => s.cards), [sections]);
  const rowById = useMemo(() => new Map(allCards.flatMap((c) => c.rows.map((r) => [r.id, r]))), [allCards]);

  const chosen = (selId && rowById.get(selId)) || null;
  const selCard = (selId && allCards.find((c) => c.rows.some((r) => r.id === selId))) || null;
  // Searching or filtering by status opens every matching card — otherwise what you asked for would be hiding behind a folded heading.
  const forceOpen = !!search.trim() || status !== 'all';
  const isFolded = (c) => !forceOpen && (c.key in fold ? fold[c.key] : c.defaultFolded);
  const allOpen = allCards.every((c) => !isFolded(c));

  // Pressing anywhere that is not a card, the side panel or a window ends the focus.
  useEffect(() => {
    if (!focusOn || !selId) return undefined;
    function onDown(e) {
      if (e.target.closest && e.target.closest('.wqc, .detail-panel-col, .wqm, .dp__pop, .op-pop, .wq-focus')) return;
      setSelId(null);
      setPicked(new Set());
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [focusOn, selId]);

  if (loading || !tenant) return <LoadingState />;

  const ctx = { itemsById, scope, groups: inv.data?.groups, caById };
  const failed = tab === 'microsoft' ? (ss.error || !ss.data || ss.data.error) : (ctl.error || (ctl.data && ctl.data.error));
  const waiting = tab === 'microsoft' ? (ss.loading && !ss.data) : ((ctl.loading && !ctl.data) || (inv.loading && !inv.data));
  const toggleAll = () => setFold(Object.fromEntries(allCards.map((c) => [c.key, allOpen])));
  const pickRow = (card, row) => { setSelId(row.id); if (selCard?.key !== card.key) setPicked(new Set()); };
  const activateCard = (card) => { if (selCard?.key !== card.key) { setSelId(card.rows[0]?.id || null); setPicked(new Set()); } };
  const tickRow = (card, row) => {
    if (selCard?.key !== card.key) { setSelId(row.id); setPicked(new Set([row.id])); return; }
    setPicked((prev) => { const next = new Set(prev); if (next.has(row.id)) next.delete(row.id); else next.add(row.id); return next; });
  };
  const onTab = (nextTab, nextFw) => { setTab(nextTab); setFw(nextFw); setSelId(null); setPicked(new Set()); };

  const fwTile = (key, name, cov, digits) => {
    const t = pctTone(cov);
    const s = FW_STYLE[key];
    return {
      key,
      name,
      bg: s.bg,
      fg: s.fg,
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{s.icon}</svg>,
      status: { text: pctText(cov, digits), tone: t || 'mute' },
      active: key === 'microsoft' ? tab === 'microsoft' : tab === 'frameworks' && (key === 'all' ? !fw : fw === key),
      onClick: () => (key === 'microsoft' ? onTab('microsoft', null) : onTab('frameworks', key === 'all' ? null : key)),
    };
  };
  // Secure Score first, then the frameworks, and Alle (all of them together) last.
  const tiles = [
    fwTile('microsoft', 'Secure Score', microsoftCoverage, 2),
    ...FRAMEWORKS.map((f) => fwTile(f.key, f.label, covOf(f.key))),
    fwTile('all', 'Alle', covOf('all')),
  ];

  const renderRow = (card, r) => {
    const note = decided.get(r.itemKey);
    const ticked = selCard?.key === card.key && picked.has(r.id);
    const current = selCard?.key === card.key && (picked.size > 0 ? ticked : r.id === selId);
    const tone = r.status === 'notFound' ? 'bad' : r.status === 'partial' ? 'warn' : r.status === 'covered' ? 'ok' : null;
    // Only what cannot be assessed says why in the row; the card already says the rest.
    const value = r.value || (cardOf(r.status) === 'other' ? ST[r.status]?.label || '' : '');
    return (
      <div key={r.id} data-area={r.area} className={`wqc__row${current ? ' is-sel' : ''}${note?.decisionType ? ' is-done' : ''}`} onClick={(e) => { e.stopPropagation(); pickRow(card, r); }}>
        <label className="wqc__pick" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={ticked} onChange={() => tickRow(card, r)} aria-label={`Vælg ${r.title}`} />
          <span className="wqc__cb" aria-hidden="true"><PortalIcon name="check" size={11} /></span>
        </label>
        <span className={`wqc__ic${tone ? ` wqc__ic--${tone}` : ''}`}><NcIcon name={r.kind === 'ms' ? (AREA_ICON[r.area] || 'grid') : 'shield'} size={15} /></span>
        {r.kind === 'clause' && <span className="wqc__code" title={r.code}>{r.code}</span>}
        {r.kind === 'clause' && !fw && <span className="wqc__fw">{FRAMEWORK_LABEL[r.clause.fw]}</span>}
        <span className="wqc__nm"><span className="wqc__l1"><b title={r.title}>{r.title}</b></span></span>
        {r.kind === 'ms' && <span className="wqc__meta" title={r.sub}>{r.sub}</span>}
        <span className="wqc__val" title={value}>{value}</span>
        <span className="wqc__tags">
          {itList.has(itKeyOf('cmp', card.key), String(r.id).slice(0, 200)) && <span className="itl-mark" title="På IT-opgaver"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>IT</span>}
          {note && <DecisionTag badge={getStatusBadge(note)} />}
        </span>
      </div>
    );
  };

  const renderCard = (c) => {
    const folded = isFolded(c);
    const shown = showAll.has(c.key) ? c.rows : c.rows.slice(0, FUND_ROWS);
    return (
      <StatusCard
        key={c.key}
        tone={c.tone}
        title={c.title}
        pill={c.pill}
        selected={selCard?.key === c.key}
        folded={folded}
        onFold={() => setFold((prev) => ({ ...prev, [c.key]: !folded }))}
        onActivate={() => activateCard(c)}
        rows={shown.map((r) => renderRow(c, r))}
        total={c.rows.length}
        hidden={c.rows.length - shown.length}
        onShowAll={() => setShowAll((prev) => new Set(prev).add(c.key))}
        {...cardItProps({ prefix: 'cmp', areaName: 'Compliance', card: c, selCard, picked, selId, setPicked, activateCard, itList })}
      />
    );
  };


  return (
    <div className={`arbejdsko-page cmp2${focusOn ? ' is-focus' : ''}`} ref={pageRef} style={{ '--pw': `${detailPanel.width}px`, ...toolsStyle }}>
      <div className="head">
        <div>
          <div className="eye">Compliance</div>
          <h1>Hvordan understøtter jeres Microsoft 365 kravene?</h1>
          <p>Vælg det framework, I vil sammenholde jeres Microsoft 365 med. Vurderingen er vejledende, ikke en revision.</p>
        </div>
        <div className="cfg2__tools">
          <FocusToggle on={focusOn} onToggle={toggleFocus} />
        </div>
      </div>

      <div className="workspace">
        <div className="list-panel list-panel--float wq-menu">
          <div className="wq-menu__card mc-frame">
            <TileMenu tiles={tiles} label="Framework" />
            <div className="list-panel__filter">
              <div className="wq-seg" role="group" aria-label="Status">
                {STATUS_TABS.map(([key, name]) => (
                  <button key={key} type="button" className={`wq-seg__btn${status === key ? ' is-on' : ''}`} onClick={() => setStatus(key)}>
                    {DOT_TONE[key] && <span className={`wq-seg__dot wq-seg__dot--${DOT_TONE[key]}`} />}{name}
                  </button>
                ))}
              </div>
              <div className="wq-menu__right">
                {!forceOpen && allCards.length > 0 && <ExpandAll allOpen={allOpen} onClick={toggleAll} />}
                <SearchInput value={search} onChange={setSearch} placeholder={tab === 'microsoft' ? 'Søg kontrol...' : 'Søg krav...'} />
              </div>
            </div>
          </div>
          <div className="list-panel__gap" />
          <div className={`list-panel__body wq-tray wq-tray--cards${focusOn && chosen ? ' has-sel' : ''}`}>
            {waiting && <LoadingState />}
            {!waiting && failed && <div className="state-empty"><p>{CANNOT_FETCH}</p></div>}
            {!waiting && !failed && sections.length === 0 && <div className="state-empty"><p>{tab === 'microsoft' ? 'Ingen kontroller matcher filteret.' : 'Ingen krav matcher filteret.'}</p></div>}
            {!waiting && !failed && sections.map((s) => <div key={s.key} className="cmp2__cards">{s.cards.map(renderCard)}</div>)}
            {!waiting && !failed && tab !== 'microsoft' && unassessed && unassessed.count > 0 && (
              <p className="cfg-none cmp2__note">{unassessed.count} af {unassessed.total} politikker er ikke knyttet til et krav og påvirker ikke vurderingen. <button type="button" className="cmp-link" onClick={() => navigate(`/tenant/${tid}/configuration`)}>Se dem under Konfiguration →</button></p>
            )}
          </div>
        </div>

        <div className={`resize-handle${detailPanel.dragging ? ' is-dragging' : ''}`} onMouseDown={detailPanel.onMouseDown} />

        <div className="detail-panel-col" style={{ width: detailPanel.width }}>
          <div className="detail-panel">
            {chosen
              ? (chosen.kind === 'ms'
                ? <ControlPanel key={chosen.id} control={chosen.control} note={decided.get(chosen.itemKey)} />
                : <ClausePanel key={chosen.id} clause={chosen.clause} ctx={ctx} tenantId={tid} note={decided.get(chosen.itemKey)} />)
              : (
                <div className="wq-empty">
                  <div className="wq-empty__slot">
                    <span className="wq-empty__ic" aria-hidden="true"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg></span>
                    <b>{tab === 'microsoft' ? 'Vælg en kontrol' : 'Vælg et krav'}</b>
                    <small>Forklaring, anbefaling og fakta vises her.</small>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
